import { DATA_SOURCES } from '@/config/constants';
import { useEntity } from 'utils/hooks/api';
import useEntityBase, { isEmptyRow } from './useEntityBase';
import usePopularity from 'utils/hooks/usePopularity';
import useTikTokWeeks from 'utils/hooks/useTikTokWeeks';

/**
 * One pop-index entity - a brand or a category - with its row and the three
 * source series the index is stitched from.
 *
 * The weeks come from TikTok's hook, which the week-scoped related-categories
 * tab needs; the row query itself is monthly.
 */
const usePopIndexEntity = ({
    entityType,
    entityId,
    country,
    controller,
    coverage,
}) => {
    const entityQuery = useEntity({
        id: entityId,
        entityType,
        dataSource: DATA_SOURCES.POPULARITY,
    });
    const entity = entityQuery.data;
    const { week, year, query: weeksQuery } = useTikTokWeeks();

    const rowQuery = usePopularity({
        paginateBy: entityType,
        [entityType]: entityId ? [entityId] : null,
        countries: country ? [country.id] : null,
        enabled: !!(country && entityId),
    });
    const entry = rowQuery.data?.[0] || {};
    const row = entry.data;
    // The row carries the entity's own record as its sidecar.
    const details = entity || entry.paginateItem || null;
    const name = details?.name || null;

    return useEntityBase({
        dataSource: DATA_SOURCES.POPULARITY,
        slug: 'pop-index',
        // The segment category links carry; pop-index links omit it.
        pathSlug: null,
        entityType,
        entityId,
        controller,
        entity,
        entityQuery,
        details,
        country,
        week,
        year,
        weeksQuery,
        row,
        rowQuery,
        entry,
        name,
        exportEntity: name || 'brand',
        // No fallback on purpose: an unresolved country renders "(undefined)"
        // in the download title.
        timeSeriesName: `${name} (${country?.code || country?.name})`,
        // Until the country resolves the row query is idle, which is not loading.
        isLoading: rowQuery.isLoading || weeksQuery.isLoading || !country,
        isEmpty: isEmptyRow({
            rowQuery,
            row,
            volumeKey: 'popularity_index_avg_12_month',
        }),
        coverage,
    });
};

/** The siblings the category score card compares this entity against. */
export const usePopIndexSiblings = ({ parentCategory, country }) => {
    const query = usePopularity({
        paginateBy: 'categories',
        categories: parentCategory ? parentCategory.children : null,
        countries: country ? [country.id] : null,
        enabled: !!(parentCategory && country),
    });
    return { siblings: query.data, ready: !!query.data };
};

export default usePopIndexEntity;
